import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {UsersService} from '../../../services/users/users.service';
import {TeamCompanyUsersService} from '../../../services/teamcompanyusers/teamcompanyusers.service';
import {TeamCompaniesService} from '../../../services/teamcompanies/teamcompanies.service';

export let rememberedBrokerId = '';

@Component({
  selector: 'app-broker',
  templateUrl: './broker.component.html',
  styleUrls: ['./broker.component.scss']
})
export class BrokerComponent implements OnInit, OnDestroy {

  public allLinks = [
    { title: 'Profile', link: '/brokeragency/broker/broker-profile' },
    { title: 'Agencies', link: '/brokeragency/broker/broker-agencies' },
    { title: 'Products', link: '/brokeragency/broker/broker-products' },
  ];

  public links = this.allLinks;

  public activeLink: { title: string; link: string } | undefined;
  public isSuperAdmin = false;
  public selectedBrokerId = '';
  public brokerName = '';
  public brokers: any[] = [];
  public brokerAgencyCounts: {[brokerId: string]: number} = {};
  public info = [
    {title: 'Agencies', value: 0, background: 'lightskyblue'},
    {title: 'Products', value: 0, background: 'orange'},
  ];

  private subs: Subscription[] = [];
  private brokersLoaded = false;
  private pendingRouteBrokerId: string | null = null;

  constructor(
      private router: Router,
      private route: ActivatedRoute,
      private usersService: UsersService,
      private teamCompaniesService: TeamCompaniesService,
      private teamCompanyUsersService: TeamCompanyUsersService
  ) { }

  ngOnInit(): void {
    this.selectedBrokerId = rememberedBrokerId;

    this.setActiveLinkFromUrl();

    this.subs.push(
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
              this.setActiveLinkFromUrl();
            })
    );

    this.subs.push(
        this.route.paramMap.subscribe((pm) => {
          const id = pm.get('id');
          if (id) {
            this.pendingRouteBrokerId = id;
            this.applyBrokerIdFromRouteIfPossible();
          }
        })
    );

    this.usersService.meSubscribe((me: any) => {
      if (me) {
        this.isSuperAdmin = this.usersService.isAdmin();
        this.links = this.isSuperAdmin ? this.allLinks : this.allLinks.slice(0, -1);

        // If the current active tab becomes unavailable (Products hidden), fallback.
        const activeTitle = this.activeLink?.title;
        if (activeTitle && !this.links.some(l => l.title === activeTitle)) {
          this.activeLink = this.links[0];
          this.ensureUrlHasBrokerId();
        }
      }
    });

    this.teamCompaniesService.getCompanies().then((companies: any[]) => {
      const allCompanies = companies || [];
      this.brokerAgencyCounts = this.getBrokerAgencyCounts(allCompanies);
      this.brokers = allCompanies.filter((company: { type: string }) => company.type === 'broker');
      this.brokersLoaded = true;

      if (this.pendingRouteBrokerId) {
        if (this.setBrokerByIdIfValid(this.pendingRouteBrokerId)) {
          this.ensureUrlHasBrokerId();
          return;
        }
      }

      if (this.selectedBrokerId) {
        if (this.setBrokerByIdIfValid(this.selectedBrokerId)) {
          this.ensureUrlHasBrokerId();
          return;
        }
      }

      if (this.brokers.length > 0) {
        this.setBrokerById(this.brokers[0].id);
        this.ensureUrlHasBrokerId();
        return;
      }

      const roleBrokerId = this.teamCompanyUsersService.roles.broker?.id;
      if (roleBrokerId) {
        this.setBrokerById(roleBrokerId);
        this.ensureUrlHasBrokerId();
      }
      this.brokerName = this.teamCompanyUsersService.roles.broker?.name || '';
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
  }

  setCount(fieldName: string, value: number): void {
    const item = this.info.find(i => i.title === fieldName);
    if (item) {
      item.value = value;
      this.info = this.info.slice();
    }
  }

  gotoTab(linkObj: { title: string; link: string }): void {
    this.activeLink = linkObj;
    this.ensureUrlHasBrokerId();
  }

  setBrokerId(id: any): void {
    if (!id) {
      return;
    }
    const nextId = String(id);
    if (String(this.selectedBrokerId) === nextId) {
      return;
    }
    this.setBrokerById(nextId);
    this.ensureUrlHasBrokerId();
  }

  setBroker(event: any): void {
    this.setBrokerId(event?.value);
  }

  private getBrokerAgencyCounts(companies: any[]): {[brokerId: string]: number} {
    return companies.reduce((counts, company: { parentId: string; type: string }) => {
      if (company.type === 'agency' && company.parentId) {
        counts[company.parentId] = (counts[company.parentId] || 0) + 1;
      }
      return counts;
    }, {});
  }

  private setBrokerById(id: any): void {
    this.selectedBrokerId = String(id);
    rememberedBrokerId = this.selectedBrokerId;
  }

  private setBrokerByIdIfValid(id: string): boolean {
    if (!id) {
      return false;
    }
    const exists = this.brokers.some(b => String(b.id) === String(id));
    if (!exists) {
      return false;
    }
    this.setBrokerById(id);
    return true;
  }

  private applyBrokerIdFromRouteIfPossible(): void {
    if (!this.pendingRouteBrokerId) {
      return;
    }
    if (!this.brokersLoaded) {
      return;
    }
    if (this.setBrokerByIdIfValid(this.pendingRouteBrokerId)) {
      return;
    }
    if (this.brokers.length > 0) {
      this.setBrokerById(this.brokers[0].id);
      this.ensureUrlHasBrokerId();
    }
  }

  private setActiveLinkFromUrl(): void {
    const path = this.router.url.split('?')[0];
    const parts = path.split('/').filter(Boolean);

    // links are /brokeragency/broker/broker-profile (3 segments after root)
    // url may be /brokeragency/broker/broker-profile/123, so drop id
    const base = parts.length >= 3 ? `/${parts.slice(0, 3).join('/')}` : `/${parts.join('/')}`;

    const found = this.links.find(l => l.link === base) || this.allLinks.find(l => l.link === base);
    this.activeLink = found || this.links[0];
  }

  private ensureUrlHasBrokerId(): void {
    const linkObj = this.activeLink || this.links[0];

    if (!this.selectedBrokerId) {
      this.router.navigateByUrl(linkObj.link);
      return;
    }

    // If Products is hidden, don't navigate there.
    if (!this.links.some(l => l.title === linkObj.title)) {
      this.activeLink = this.links[0];
    }

    const safeLink = (this.activeLink || this.links[0]).link;
    const target = `${safeLink}/${this.selectedBrokerId}`;
    const current = this.router.url.split('?')[0];

    if (current === target) {
      return;
    }

    this.router.navigateByUrl(target);
  }
}
